import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye } from 'lucide-react';
import { HeadlineOverlay } from '@/store/videoFeatures/types/headline-overlay';
import { HeadlineMetricsOverview } from './metrics/HeadlineMetricsOverview';
import { HeadlineRetentionChart } from './metrics/HeadlineRetentionChart';
import { HeadlineTestMetrics } from '@/services/headlineTestService';

interface HeadlineMetricsTabProps {
  headlineOverlay: HeadlineOverlay;
  updateHeadlineOverlay?: (key: keyof HeadlineOverlay, value: any) => void;
  headlineTestId?: string;
  metricsData?: HeadlineTestMetrics[] | null;
  isLoadingMetrics?: boolean;
  startDate?: Date;
  endDate?: Date;
  onStartDateChange?: (date: Date) => void;
  onEndDateChange?: (date: Date) => void;
  onRefreshMetrics?: () => void;
}

export const HeadlineMetricsTab: React.FC<HeadlineMetricsTabProps> = ({
  headlineOverlay,
  updateHeadlineOverlay,
  headlineTestId,
  metricsData,
  isLoadingMetrics,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onRefreshMetrics,
}) => {
  useLanguage();
  const isEnabled = headlineOverlay.enabled;

  if (!isEnabled) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Eye className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold text-foreground">{translateInterfaceText("Headline não ativada")}</h3>
            <p className="text-sm text-muted-foreground text-center">
              {translateInterfaceText("Ative o recurso de headline para visualizar as métricas")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="py-4 space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-foreground">
          {translateInterfaceText("Métricas do Teste A/B")}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {translateInterfaceText("Resultados em tempo real das variações de headline")}</p>
      </div>

      {/* Tabs internas com estilo premium azul */}
      <Tabs defaultValue="metrics" className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-11 bg-[#F1F5F9] p-1 rounded-xl" style={{ minWidth: '300px' }}>
          <TabsTrigger
            value="metrics"
            className="h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
          >
            {translateInterfaceText("Métricas")}</TabsTrigger>
          <TabsTrigger
            value="retention"
            className="h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
          >
            {translateInterfaceText("Retenção")}</TabsTrigger>
        </TabsList>

        <TabsContent value="metrics" className="mt-6">
          <HeadlineMetricsOverview
            headlineOverlay={headlineOverlay}
            headlineTestId={headlineTestId}
            metricsData={metricsData}
            isLoadingMetrics={isLoadingMetrics}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
            onRefreshMetrics={onRefreshMetrics}
          />
        </TabsContent>

        <TabsContent value="retention" className="mt-6">
          <HeadlineRetentionChart
            headlineOverlay={headlineOverlay}
            metricsData={metricsData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
